import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AppLayout } from '../../components/layout/AppLayout';
import { supabase } from '../../api/supabase';
import theme from '../../theme';

const isWeb = Platform.OS === 'web';

interface CashMetrics {
  totalIncome: number;
  totalExpenses: number;
  netCashFlow: number;
  bankBalance: number;
  pendingDeposits: number;
  pendingPayments: number;
}

interface WeeklySummary {
  week_start_date: string;
  week_end_date: string;
  total_income: number;
  total_expenses: number;
  net_cash_flow: number;
  total_transactions: number;
}

interface RecentTransaction {
  id: string;
  transaction_date: string;
  transaction_type: string;
  category: string;
  amount: number;
  payment_method: string;
  reference_number: string;
  notes: string;
  verified: boolean;
}

type TimeFilter = 'week' | 'month' | 'quarter' | 'year';

export const CompanyCashDashboard = () => {
  const [metrics, setMetrics] = useState<CashMetrics>({
    totalIncome: 0,
    totalExpenses: 0,
    netCashFlow: 0,
    bankBalance: 0,
    pendingDeposits: 0,
    pendingPayments: 0,
  });
  const [weeklySummaries, setWeeklySummaries] = useState<WeeklySummary[]>([]);
  const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('month');

  useEffect(() => {
    loadDashboardData();
  }, [timeFilter]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadMetrics(),
        loadWeeklySummaries(),
        loadRecentTransactions(),
      ]);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMetrics = async () => {
    const dateFilter = getDateFilter();

    // Get all transactions in period
    const { data: transactions, error } = await supabase
      .from('company_cash_transactions')
      .select('transaction_type, category, amount, verified')
      .gte('transaction_date', dateFilter.startDate)
      .lte('transaction_date', dateFilter.endDate);

    if (error) {
      console.error('Error loading metrics:', error);
      return;
    }

    let totalIncome = 0;
    let totalExpenses = 0;
    let pendingDeposits = 0;
    let pendingPayments = 0;

    transactions?.forEach(tx => {
      const isIncome = tx.transaction_type === 'bank_deposit' || 
                       tx.transaction_type === 'driver_payment' || 
                       tx.transaction_type === 'other_income' ||
                       tx.transaction_type === 'cash_deposit';
      
      if (isIncome) {
        totalIncome += tx.amount;
        if (!tx.verified) pendingDeposits += tx.amount;
      } else {
        totalExpenses += tx.amount;
        if (!tx.verified) pendingPayments += tx.amount;
      }
    });

    setMetrics({
      totalIncome,
      totalExpenses,
      netCashFlow: totalIncome - totalExpenses,
      bankBalance: totalIncome - totalExpenses, // Simplified - could query actual bank balance
      pendingDeposits,
      pendingPayments,
    });
  };

  const loadWeeklySummaries = async () => {
    const dateFilter = getDateFilter();

    const { data, error } = await supabase
      .from('weekly_cash_flow_summary')
      .select('*')
      .gte('week_start_date', dateFilter.startDate)
      .lte('week_end_date', dateFilter.endDate)
      .order('week_start_date', { ascending: false })
      .limit(8);

    if (error) {
      console.error('Error loading weekly summaries:', error);
      return;
    }

    setWeeklySummaries(data || []);
  };

  const loadRecentTransactions = async () => {
    const { data, error } = await supabase
      .from('company_cash_transactions')
      .select('*')
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) {
      console.error('Error loading transactions:', error);
      return;
    }

    setRecentTransactions(data || []);
  };

  const getDateFilter = () => {
    const today = new Date();
    let startDate = new Date();

    switch (timeFilter) {
      case 'week':
        startDate.setDate(today.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(today.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(today.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(today.getFullYear() - 1);
        break;
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0],
    };
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadDashboardData();
    setRefreshing(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getCategoryIcon = (category: string) => {
    const iconMap: Record<string, string> = {
      uber_deposit: 'car',
      careem_deposit: 'car',
      driver_rent_collection: 'people',
      salik_bill_payment: 'card',
      fines_payment: 'warning',
      maintenance: 'build',
      fuel: 'water',
      insurance: 'shield-checkmark',
      salaries: 'wallet',
      office_rent: 'business',
      utilities: 'flash',
      office_supplies: 'cube',
      petty_cash: 'cash',
      other_expense: 'ellipsis-horizontal',
      other_income: 'trending-up',
    };
    return iconMap[category] || 'help-circle';
  };

  const getCategoryColor = (transactionType: string) => {
    return transactionType === 'bank_deposit' || 
           transactionType === 'driver_payment' || 
           transactionType === 'other_income' ||
           transactionType === 'cash_deposit'
      ? theme.colors.success
      : theme.colors.error;
  };

  const getCategoryLabel = (category: string) => {
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <AppLayout title="Company Cash">
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.primary} />
          <Text style={styles.loadingText}>Loading financial data...</Text>
        </View>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Company Cash">
      <ScrollView
        style={styles.container}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {/* Time Filter */}
        <View style={styles.filterContainer}>
          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
            {(['week', 'month', 'quarter', 'year'] as TimeFilter[]).map((filter) => (
              <TouchableOpacity
                key={filter}
                style={[
                  styles.filterButton,
                  timeFilter === filter && styles.filterButtonActive,
                ]}
                onPress={() => setTimeFilter(filter)}
              >
                <Text
                  style={[
                    styles.filterButtonText,
                    timeFilter === filter && styles.filterButtonTextActive,
                  ]}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {/* Key Metrics */}
        <View style={styles.metricsGrid}>
          {/* Net Cash Flow - Most Important */}
          <View style={[styles.metricCard, styles.metricCardLarge]}>
            <View style={styles.metricHeader}>
              <Ionicons
                name="analytics"
                size={24}
                color={metrics.netCashFlow >= 0 ? theme.colors.success : theme.colors.error}
              />
              <Text style={styles.metricLabel}>Net Cash Flow</Text>
            </View>
            <Text
              style={[
                styles.metricValueLarge,
                { color: metrics.netCashFlow >= 0 ? theme.colors.success : theme.colors.error },
              ]}
            >
              {formatCurrency(metrics.netCashFlow)}
            </Text>
            <Text style={styles.metricSubtext}>
              {timeFilter === 'week' ? 'Last 7 days' : 
               timeFilter === 'month' ? 'Last 30 days' :
               timeFilter === 'quarter' ? 'Last 90 days' : 'Last 12 months'}
            </Text>
          </View>

          {/* Total Income */}
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="arrow-down-circle" size={20} color={theme.colors.success} />
              <Text style={styles.metricLabel}>Total Income</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.success }]}>
              {formatCurrency(metrics.totalIncome)}
            </Text>
          </View>

          {/* Total Expenses */}
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="arrow-up-circle" size={20} color={theme.colors.error} />
              <Text style={styles.metricLabel}>Total Expenses</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.error }]}>
              {formatCurrency(metrics.totalExpenses)}
            </Text>
          </View>

          {/* Pending Deposits */}
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="time" size={20} color={theme.colors.warning} />
              <Text style={styles.metricLabel}>Pending Deposits</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.warning }]}>
              {formatCurrency(metrics.pendingDeposits)}
            </Text>
          </View>

          {/* Pending Payments */}
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="hourglass" size={20} color={theme.colors.warning} />
              <Text style={styles.metricLabel}>Pending Payments</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.warning }]}>
              {formatCurrency(metrics.pendingPayments)}
            </Text>
          </View>
        </View>

        {/* Weekly Summaries */}
        {weeklySummaries.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Weekly Cash Flow</Text>
            {weeklySummaries.map((week, index) => (
              <View key={index} style={styles.weekCard}>
                <View style={styles.weekHeader}>
                  <Text style={styles.weekDate}>
                    {formatDate(week.week_start_date)} - {formatDate(week.week_end_date)}
                  </Text>
                  <Text style={styles.weekTransactions}>
                    {week.total_transactions} transactions
                  </Text>
                </View>
                <View style={styles.weekStats}>
                  <View style={styles.weekStat}>
                    <Text style={styles.weekStatLabel}>Income</Text>
                    <Text style={[styles.weekStatValue, { color: theme.colors.success }]}>
                      +{formatCurrency(week.total_income)}
                    </Text>
                  </View>
                  <View style={styles.weekStat}>
                    <Text style={styles.weekStatLabel}>Expenses</Text>
                    <Text style={[styles.weekStatValue, { color: theme.colors.error }]}>
                      -{formatCurrency(week.total_expenses)}
                    </Text>
                  </View>
                  <View style={styles.weekStat}>
                    <Text style={styles.weekStatLabel}>Net</Text>
                    <Text
                      style={[
                        styles.weekStatValue,
                        styles.weekStatValueBold,
                        { color: week.net_cash_flow >= 0 ? theme.colors.success : theme.colors.error },
                      ]}
                    >
                      {formatCurrency(week.net_cash_flow)}
                    </Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}

        {/* Recent Transactions */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Recent Transactions</Text>
            <TouchableOpacity>
              <Text style={styles.sectionLink}>View All</Text>
            </TouchableOpacity>
          </View>
          {recentTransactions.map((transaction) => (
            <View key={transaction.id} style={styles.transactionCard}>
              <View style={styles.transactionIconContainer}>
                <Ionicons
                  name={getCategoryIcon(transaction.category) as any}
                  size={24}
                  color={getCategoryColor(transaction.transaction_type)}
                />
              </View>
              <View style={styles.transactionContent}>
                <Text style={styles.transactionCategory}>
                  {getCategoryLabel(transaction.category)}
                </Text>
                <Text style={styles.transactionDate}>
                  {formatDate(transaction.transaction_date)}
                  {transaction.reference_number && ` • ${transaction.reference_number}`}
                </Text>
                {transaction.notes && (
                  <Text style={styles.transactionNotes} numberOfLines={1}>
                    {transaction.notes}
                  </Text>
                )}
              </View>
              <View style={styles.transactionRight}>
                <Text
                  style={[
                    styles.transactionAmount,
                    { color: getCategoryColor(transaction.transaction_type) },
                  ]}
                >
                  {transaction.transaction_type.includes('deposit') || 
                   transaction.transaction_type === 'driver_payment' || 
                   transaction.transaction_type === 'other_income' ? '+' : '-'}
                  {formatCurrency(transaction.amount)}
                </Text>
                {!transaction.verified && (
                  <View style={styles.unverifiedBadge}>
                    <Ionicons name="alert-circle" size={12} color={theme.colors.warning} />
                    <Text style={styles.unverifiedText}>Unverified</Text>
                  </View>
                )}
              </View>
            </View>
          ))}
        </View>
      </ScrollView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  filterContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginRight: 8,
    borderRadius: 20,
    backgroundColor: theme.colors.gray100,
  },
  filterButtonActive: {
    backgroundColor: theme.colors.primary,
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  filterButtonTextActive: {
    color: theme.colors.white,
  },
  metricsGrid: {
    padding: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  metricCard: {
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 12,
    width: isWeb ? 'calc(50% - 6px)' : '48%',
    ...theme.shadows.small,
  },
  metricCardLarge: {
    width: '100%',
    borderLeftWidth: 4,
    borderLeftColor: theme.colors.primary,
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  metricLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginLeft: 8,
    fontWeight: '500',
  },
  metricValue: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  metricValueLarge: {
    fontSize: 32,
    fontWeight: '800',
    marginBottom: 4,
  },
  metricSubtext: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  section: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: theme.colors.text,
  },
  sectionLink: {
    fontSize: 14,
    color: theme.colors.primary,
    fontWeight: '600',
  },
  weekCard: {
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    ...theme.shadows.small,
  },
  weekHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  weekDate: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
  },
  weekTransactions: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  weekStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  weekStat: {
    flex: 1,
  },
  weekStatLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  weekStatValue: {
    fontSize: 16,
    fontWeight: '600',
  },
  weekStatValueBold: {
    fontWeight: '700',
  },
  transactionCard: {
    flexDirection: 'row',
    backgroundColor: theme.colors.white,
    padding: 16,
    borderRadius: 12,
    marginBottom: 8,
    alignItems: 'center',
    ...theme.shadows.small,
  },
  transactionIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.colors.gray100,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  transactionContent: {
    flex: 1,
  },
  transactionCategory: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 4,
  },
  transactionDate: {
    fontSize: 13,
    color: theme.colors.textSecondary,
  },
  transactionNotes: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
    fontStyle: 'italic',
  },
  transactionRight: {
    alignItems: 'flex-end',
  },
  transactionAmount: {
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  unverifiedBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.warning + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  unverifiedText: {
    fontSize: 10,
    color: theme.colors.warning,
    marginLeft: 4,
    fontWeight: '600',
  },
});
